import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Startup experience "Il territorio si accende e diventa Fuma c'anduma World".
 * Digital rain verde neon → convergenza al centro → logo + slogan → i flussi
 * diventano nodi/strade geografici, ponte verso la Mappa Viva.
 * - Una sola volta per sessione (sessionStorage). Prima apertura assoluta = versione più scenografica.
 * - Non blocca l'accesso: l'app fa boot in parallelo dietro l'overlay; se pronta prima, chiude prima.
 * - Rispetta prefers-reduced-motion. Nessun audio.
 */
const SESSION_KEY = "fuma_intro_session";
const FIRST_KEY = "fuma_intro_first_done";

// Stato a livello di modulo: sopravvive ai re-mount di React StrictMode nello stesso load.
let INTRO_STATE = null; // { first, reduced } deciso una volta per caricamento pagina
let INTRO_DONE = false;

const GLYPHS = "01FUMAWORLDXPAIfumacanduma·°/\\|<>=+◦△○□◇✕—NESW23456789".split("");
const WORDS = ["FUMA", "WORLD", "RIDER", "LOCAL", "AI", "XP", "FOOD", "EVENT",
  "45.13N", "7.60E", "CANAVESE", "QUARTIERE", "COMMUNITY", "MAP", "45.23N", "7.72E"];

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function StartupIntro() {
  const { loading } = useAuth();
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const doneRef = useRef(false);
  const loadingRef = useRef(loading);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  const [state] = useState(() => {
    if (typeof window === "undefined") return { show: false };
    if (INTRO_DONE) return { show: false };
    if (INTRO_STATE) return { show: true, ...INTRO_STATE }; // re-mount StrictMode: continua
    try {
      if (sessionStorage.getItem(SESSION_KEY)) { INTRO_DONE = true; return { show: false }; }
      sessionStorage.setItem(SESSION_KEY, "1");
      const first = !localStorage.getItem(FIRST_KEY);
      localStorage.setItem(FIRST_KEY, "1");
      INTRO_STATE = { first, reduced: prefersReduced() };
      return { show: true, ...INTRO_STATE };
    } catch (e) {
      INTRO_STATE = { first: false, reduced: false };
      return { show: true, ...INTRO_STATE };
    }
  });

  const [showText, setShowText] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [gone, setGone] = useState(!state.show);

  const T = state.first
    ? { rain: 1150, converge: 900, reveal: 1850, min: 3000 }
    : { rain: 700, converge: 560, reveal: 1250, min: 1900 };

  const beginExit = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    INTRO_DONE = true;
    cancelAnimationFrame(rafRef.current);
    setExiting(true);
    setTimeout(() => setGone(true), 480);
  };

  useEffect(() => {
    if (!state.show || gone) return undefined;

    if (state.reduced) {
      setShowText(true);
      const start = performance.now();
      const tick = () => {
        if (performance.now() - start >= 700 && !loadingRef.current) { beginExit(); return; }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafRef.current);
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let W = 0, H = 0, cols = 0, fontSize = 16, rows = 0;
    let drops = [], colWord = [];
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const setup = () => {
      W = window.innerWidth; H = window.innerHeight;
      canvas.width = Math.floor(W * dpr); canvas.height = Math.floor(H * dpr);
      canvas.style.width = W + "px"; canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      fontSize = Math.max(13, Math.min(18, Math.floor(W / 88)));
      cols = Math.ceil(W / fontSize);
      rows = Math.ceil(H / fontSize);
      // schermo già popolato: ogni colonna parte a un'altezza casuale
      drops = new Array(cols).fill(0).map(() => Math.floor(Math.random() * rows));
      colWord = new Array(cols).fill(null);
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
    };
    setup();
    window.addEventListener("resize", setup);

    const NODES = [
      [0.5, 0.62], [0.4, 0.7], [0.62, 0.68], [0.46, 0.78], [0.58, 0.8], [0.34, 0.62], [0.68, 0.58],
    ];

    startRef.current = performance.now();

    const draw = () => {
      const el = performance.now() - startRef.current;
      const convP = Math.max(0, Math.min(1, (el - T.rain) / T.converge));
      const revealing = el >= T.reveal;
      if (revealing && !showText) setShowText(true);

      ctx.fillStyle = revealing ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0.06)";
      ctx.fillRect(0, 0, W, H);

      ctx.font = `${fontSize}px "SFMono-Regular", ui-monospace, monospace`;
      ctx.textBaseline = "top";
      const cx = W / 2;
      const rainAlpha = revealing ? Math.max(0, 1 - (el - T.reveal) / 520) : 1;

      if (rainAlpha > 0.02) {
        for (let i = 0; i < cols; i++) {
          const x = i * fontSize;
          const dxN = Math.abs(x - cx) / cx;
          const edge = 1 - convP * dxN * 0.95;
          if (edge <= 0.05) { drops[i] += 1; continue; }

          let ch, bright = false;
          if (colWord[i]) {
            ch = colWord[i].text[colWord[i].idx];
            colWord[i].idx++;
            bright = true;
            if (colWord[i].idx >= colWord[i].text.length) colWord[i] = null;
          } else {
            if (el < T.rain + T.converge && Math.random() < 0.01) {
              colWord[i] = { text: WORDS[(Math.random() * WORDS.length) | 0], idx: 0 };
            }
            ch = GLYPHS[(Math.random() * GLYPHS.length) | 0];
          }
          const y = (drops[i] % (rows + 6)) * fontSize;
          ctx.globalAlpha = Math.max(0.05, Math.min(1, edge)) * rainAlpha;
          if (bright || Math.random() < 0.12) {
            ctx.fillStyle = bright ? "#DFFFE9" : "#F2FFF7";
            ctx.shadowColor = "#00E676"; ctx.shadowBlur = 10;
          } else {
            ctx.fillStyle = "#00E676"; ctx.shadowBlur = 0;
          }
          ctx.fillText(ch, x, y);
          ctx.shadowBlur = 0;
          drops[i] += 1;
          if (y > H && Math.random() > 0.94) drops[i] = Math.floor(Math.random() * -12);
        }
        ctx.globalAlpha = 1;
      }

      if (convP > 0) {
        const gw = W * 0.42 * convP;
        const lg = ctx.createLinearGradient(0, 0, gw, 0);
        lg.addColorStop(0, "rgba(0,0,0,0.96)"); lg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = lg; ctx.fillRect(0, 0, gw, H);
        const rg = ctx.createLinearGradient(W, 0, W - gw, 0);
        rg.addColorStop(0, "rgba(0,0,0,0.96)"); rg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = rg; ctx.fillRect(W - gw, 0, gw, H);
      }

      const glowP = Math.max(0, Math.min(1, (el - T.rain) / (T.reveal - T.rain)));
      if (glowP > 0) {
        const r = Math.max(W, H) * (0.16 + glowP * 0.36);
        const rad = ctx.createRadialGradient(cx, H / 2, 0, cx, H / 2, r);
        rad.addColorStop(0, `rgba(0,230,118,${0.16 * glowP})`);
        rad.addColorStop(1, "rgba(0,230,118,0)");
        ctx.fillStyle = rad; ctx.fillRect(0, 0, W, H);
      }

      if (revealing) {
        const gp = Math.max(0, Math.min(1, (el - T.reveal) / 650));
        ctx.save();
        ctx.strokeStyle = `rgba(0,230,118,${0.4 * gp})`;
        ctx.lineWidth = 1.2;
        ctx.shadowColor = "#00E676"; ctx.shadowBlur = 12 * gp;
        for (let i = 0; i < NODES.length - 1; i++) {
          ctx.beginPath();
          ctx.moveTo(NODES[i][0] * W, NODES[i][1] * H);
          ctx.lineTo(NODES[i + 1][0] * W, NODES[i + 1][1] * H);
          ctx.stroke();
        }
        for (const n of NODES) {
          ctx.beginPath();
          ctx.fillStyle = `rgba(200,255,220,${0.95 * gp})`;
          ctx.arc(n[0] * W, n[1] * H, 2 + 3 * gp, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      if (el >= T.min && !loadingRef.current) { beginExit(); return; }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => { cancelAnimationFrame(rafRef.current); window.removeEventListener("resize", setup); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.show]);

  if (gone || !state.show) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="startup-intro"
      className="fixed inset-0 z-[9999] bg-black overflow-hidden"
      style={{ transition: "opacity 0.45s ease, filter 0.45s ease", opacity: exiting ? 0 : 1, filter: exiting ? "blur(6px)" : "none", pointerEvents: exiting ? "none" : "auto" }}
    >
      {!state.reduced && <canvas ref={canvasRef} className="absolute inset-0 block" />}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center select-none"
        style={{
          transition: "opacity 0.6s ease, transform 0.6s cubic-bezier(0.16,1,0.3,1)",
          opacity: showText ? 1 : 0,
          transform: showText ? "translateY(0) scale(1)" : "translateY(10px) scale(0.96)",
        }}
      >
        <h1
          className="font-['Unbounded'] font-black leading-none text-white text-4xl sm:text-5xl lg:text-6xl"
          style={{ textShadow: "0 0 24px rgba(0,230,118,0.55), 0 0 48px rgba(0,230,118,0.25)" }}
          data-testid="startup-logo"
        >
          Fuma c'anduma
        </h1>
        <div
          className="mt-2 font-['Unbounded'] font-black text-[#00E676] tracking-[0.5em] text-lg sm:text-2xl pl-[0.5em]"
          style={{ textShadow: "0 0 18px rgba(0,230,118,0.7)" }}
        >
          WORLD
        </div>
        <div className="mt-4 h-px w-24 bg-gradient-to-r from-transparent via-[#00E676]/70 to-transparent" />
        <p className="mt-4 text-sm sm:text-base text-white/75 tracking-wide" data-testid="startup-slogan">
          L'amichevole rider di quartiere
        </p>
      </div>
    </div>
  );
}
